import { useForm } from 'react-hook-form'
import { Box, Button, Input, NativeSelect, HStack, SimpleGrid, VStack, Field } from '@chakra-ui/react';
import { FiSearch, FiX } from 'react-icons/fi'
import { useSurveyGroups } from '../../hooks/useSurvey'
import { SurveyType, SURVEY_TYPE_LABELS } from '../../types/survey'

interface SurveySearchFormProps {
  onSearch: (filters: SurveySearchFilters) => void
  onClear: () => void
}

export interface SurveySearchFilters {
  label?: string
  questionText?: string
  type?: SurveyType
  cycle?: number
  surveyGroupId?: string
  isActive?: boolean
}

export function SurveySearchForm({ onSearch, onClear }: SurveySearchFormProps) {
  const { data: groups } = useSurveyGroups()

  const { register, handleSubmit, reset } = useForm<SurveySearchFilters>({
    defaultValues: {
      label: '',
      questionText: '',
      type: undefined,
      cycle: undefined,
      surveyGroupId: '',
      isActive: undefined,
    },
  })

  const handleFormSubmit = (data: SurveySearchFilters) => {
    // Clean up empty strings to undefined
    const cleanData: SurveySearchFilters = {
      label: data.label || undefined,
      questionText: data.questionText || undefined,
      type: data.type,
      cycle: data.cycle,
      surveyGroupId: data.surveyGroupId === 'none'
        ? '00000000-0000-0000-0000-000000000000' // Guid.Empty for "no group"
        : data.surveyGroupId || undefined,
      isActive: data.isActive,
    }
    onSearch(cleanData)
  }

  const handleClear = () => {
    reset()
    onClear()
  }

  // Generate recent year options (current year + 2 previous years)
  const currentYear = new Date().getFullYear()
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2]

  return (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <VStack gap={4} align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            <Field.Root>
              <Field.Label fontSize="sm">Label</Field.Label>
              <Input size="sm" placeholder="Search by label..." {...register('label')} />
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="sm">Question Text</Field.Label>
              <Input size="sm" placeholder="Search in question..." {...register('questionText')} />
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="sm">Type</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  placeholder="All types"
                  {...register('type', { setValueAs: v => v === '' ? undefined : Number(v) })}>
                  {Object.entries(SURVEY_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="sm">Cycle Year</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  placeholder="All years"
                  {...register('cycle', { setValueAs: v => v === '' ? undefined : Number(v) })}>
                  {yearOptions.map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="sm">Group</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  placeholder="All groups"
                  {...register('surveyGroupId', { setValueAs: v => v || undefined })}>
                  <option value="none">(No Group)</option>
                  {groups?.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.label}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>

            <Field.Root>
              <Field.Label fontSize="sm">Status</Field.Label>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  placeholder="All statuses"
                  {...register('isActive', { setValueAs: v => v === '' ? undefined : v === 'true' })}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Field.Root>
          </SimpleGrid>

          <HStack justify="flex-end" pt={2}>
            <Button variant="ghost" size="sm" onClick={handleClear}><FiX />Clear
                          </Button>
            <Button colorPalette="brand" type="submit" size="sm"><FiSearch />Search
                          </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
}
